import { useEffect, useState, useCallback, useMemo } from "react";
import { usePersistedState } from "@/lib/usePersistedState";
import { fmtDate } from "@/components/DatePicker";
import api, { usd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Download, ShieldCheck, ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { OfflineBadge } from "@/components/InstallPrompt";

function Row({ label, value, strong, accent }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${strong ? "font-semibold" : "text-muted-foreground"}`}>{label}</span>
      <span className={`font-mono-num ${strong ? "font-semibold" : ""} ${accent || ""}`}>{value}</span>
    </div>
  );
}

export default function Payslips() {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const [slips, setSlips] = useState([]);
  const [active, setActive] = useState(null);
  const [empFilter, setEmpFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const employees = [...new Map(slips.map((s) => [s.employee_id || s.employee_name, s.employee_name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const shown = slips
    .filter((s) => empFilter === "all" || (s.employee_id || s.employee_name) === empFilter)
    .sort((a, b) => (sort === "newest" ? (b.pay_date || "").localeCompare(a.pay_date || "") : (a.pay_date || "").localeCompare(b.pay_date || "")));

  const [open, setOpen] = usePersistedState("paystub-tree", {});
  const tree = useMemo(() => {
    const years = {};
    shown.forEach((s) => { const y = (s.pay_date || "").slice(0, 4) || "—", m = (s.pay_date || "").slice(0, 7) || "—"; years[y] = years[y] || { months: {}, slips: [] }; years[y].slips.push(s); const mo = (years[y].months[m] = years[y].months[m] || { emps: {}, slips: [] }); mo.slips.push(s); const k = s.employee_name || "—"; (mo.emps[k] = mo.emps[k] || []).push(s); });
    const dir = sort === "newest" ? -1 : 1;
    return Object.keys(years).sort((a, b) => dir * a.localeCompare(b)).map((y) => ({ key: y, slips: years[y].slips, months: Object.keys(years[y].months).sort((a, b) => dir * a.localeCompare(b)).map((m) => ({ key: m, label: new Date(`${m}-15T00:00:00`).toLocaleString("en-US", { month: "long" }), slips: years[y].months[m].slips, emps: Object.keys(years[y].months[m].emps).sort((a, b) => a.localeCompare(b)).map((e) => ({ key: `${m}|${e}`, name: e, slips: years[y].months[m].emps[e] })) })) }));
  }, [shown, sort]);
  const isOpen = (k, dflt) => (k in open ? open[k] : dflt);
  const setAll = (v) => { const o = {}; tree.forEach((y) => { o[`y|${y.key}`] = v; y.months.forEach((m) => { o[`m|${m.key}`] = v; m.emps.forEach((e) => { o[`e|${e.key}`] = v; }); }); }); setOpen(o); };
  const toggle = (k, dflt) => setOpen((o) => ({ ...o, [k]: !isOpen(k, dflt) }));
  const net = (arr) => arr.reduce((a, s) => a + (s.net_pay || 0), 0);
  const gross = (arr) => arr.reduce((a, s) => a + (s.gross_pay || 0), 0);
  const Header = ({ k, dflt, level, title, count, arr, testid }) => (
    <button type="button" data-testid={testid} onClick={() => toggle(k, dflt)} className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 text-left transition-colors ${level === 0 ? "py-2.5 bg-[#0b1a3a] text-white hover:bg-[#12285a]" : level === 1 ? "py-2 bg-muted hover:bg-muted/70 font-semibold" : "py-1.5 hover:bg-accent text-sm"}`}>
      <span className="inline-flex items-center gap-2">{isOpen(k, dflt) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}<span className={level === 0 ? "font-display font-bold text-base" : ""}>{title}</span><span className={`text-xs font-normal ${level === 0 ? "text-slate-300" : "text-muted-foreground"}`}>{count} paystub{count !== 1 ? "s" : ""}</span></span>
      <span className={`font-mono-num text-xs ${level === 0 ? "text-slate-200" : "text-muted-foreground"}`}>Gross {usd(gross(arr))} · <span className={level === 0 ? "text-emerald-300 font-semibold" : "text-emerald-600 font-semibold"}>Net {usd(net(arr))}</span></span>
    </button>
  );

  const load = useCallback(() => {
    const params = user.role === "bureau_admin" ? { company_id: activeCompanyId } : {};
    if (user.role === "bureau_admin" && !activeCompanyId) return;
    api.get("/payslips", { params }).then((r) => { setSlips(r.data); setCached(!!r.headers?.["x-rightpay-offline"] || !navigator.onLine); }).catch(() => {});
  }, [user.role, activeCompanyId]);
  const [cached, setCached] = useState(false);

  useEffect(() => { load(); }, [load]);

  const download = async (slip) => {
    try {
      const res = await api.get(`/payslips/${slip.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `Paystub_${slip.employee_name?.replace(/\s+/g, "_")}_${fmtDate(slip.pay_date).replace(/\//g, "-")}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Paystub PDF downloaded");
    } catch { toast.error(navigator.onLine ? "Could not generate PDF" : "This paystub was not saved for offline use — open it once while online"); }
  };
  const downloadLegacy = (slip) => {
    const lines = [
      `RightPay Bureau — Paystub`,
      `Employee: ${slip.employee_name}   ${slip.job_title || ""}`,
      `Pay Period: ${fmtDate(slip.period_start)} to ${fmtDate(slip.period_end)}`,
      `Pay Date: ${fmtDate(slip.pay_date)}`,
      ``,
      `EARNINGS`,
      `  Regular Pay        ${usd(slip.regular_pay)}`,
      `  Overtime Pay       ${usd(slip.overtime_pay)}`,
      `  Gross Pay          ${usd(slip.gross_pay)}`,
      ``,
      `DEDUCTIONS`,
      `  Federal Tax        ${usd(slip.federal_tax)}`,
      `  State Tax          ${usd(slip.state_tax)}`,
      `  Social Security    ${usd(slip.social_security)}`,
      `  Medicare           ${usd(slip.medicare)}`,
      `  Health Insurance   ${usd(slip.health_deduction)}`,
      `  401(k)             ${usd(slip.retirement)}`,
      `  Total Deductions   ${usd(slip.total_deductions)}`,
      ``,
      `NET PAY              ${usd(slip.net_pay)}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `paystub-${slip.employee_name}-${slip.pay_date?.slice(0, 10)}.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Paystub downloaded");
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <OfflineBadge cached={cached} />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Paystubs</h1>
          <p className="text-sm text-muted-foreground mt-1">{shown.length} of {slips.length} paystubs</p>
        </div>
        <div className="flex gap-2">
          {user.role !== "employee" && (
            <Select value={empFilter} onValueChange={setEmpFilter}>
              <SelectTrigger data-testid="paystub-employee-filter" className="w-56"><SelectValue placeholder="All employees" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" className="h-10" data-testid="paystub-expand-all" onClick={() => setAll(true)}>Expand all</Button>
          <Button variant="outline" size="sm" className="h-10" data-testid="paystub-collapse-all" onClick={() => setAll(false)}>Collapse all</Button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="paystub-sort" className="w-44"><ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="newest">Date: newest first</SelectItem><SelectItem value="oldest">Date: oldest first</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2" data-testid="paystub-tree">
        {tree.map((y, yi) => (
          <div key={y.key} className="space-y-1.5">
            <Header k={`y|${y.key}`} dflt={yi === 0} level={0} title={y.key} count={y.slips.length} arr={y.slips} testid={`year-group-${y.key}`} />
            {isOpen(`y|${y.key}`, yi === 0) && y.months.map((m, mi) => (
              <div key={m.key} className="ml-3 space-y-1">
                <Header k={`m|${m.key}`} dflt={yi === 0 && mi === 0} level={1} title={m.label} count={m.slips.length} arr={m.slips} testid={`month-group-${m.key}`} />
                {isOpen(`m|${m.key}`, yi === 0 && mi === 0) && m.emps.map((e) => (
                  <div key={e.key} className="ml-4 space-y-1">
                    <Header k={`e|${e.key}`} dflt={empFilter !== "all" || e.slips.length === 1} level={2} title={e.name} count={e.slips.length} arr={e.slips} testid={`emp-group-${e.key}`} />
                    {isOpen(`e|${e.key}`, empFilter !== "all" || e.slips.length === 1) && <div className="ml-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-2">{e.slips.sort((a, b) => (sort === "newest" ? -1 : 1) * (a.pay_date || "").localeCompare(b.pay_date || "")).map((s) => (
          <Card key={s.id} data-testid="payslip-item-card" className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActive(s)}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold font-display">{s.employee_name}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(s.pay_date)}</div>
              </div>
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary"><FileText className="w-4 h-4" /></span>
            </div>
            <div className="mt-4 space-y-1">
              <Row label="Gross" value={usd(s.gross_pay)} />
              <Row label="Deductions" value={`- ${usd(s.total_deductions)}`} accent="text-amber-600" />
              <div className="border-t border-border mt-1 pt-1"><Row label="Net Pay" value={usd(s.net_pay)} strong accent="text-emerald-600" /></div>
            </div>
          </Card>
                    ))}</div>}
                  </div>))}
              </div>))}
          </div>))}
        {slips.length === 0 && <p className="text-muted-foreground">No paystubs available.</p>}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="payslip-detail-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Paystub</DialogTitle>
            <DialogDescription>Earnings and withholdings breakdown for this pay period.</DialogDescription>
          </DialogHeader>
          {active && (
            <div>
              <div className="mb-4">
                <div className="font-semibold">{active.employee_name}</div>
                <div className="text-xs text-muted-foreground">{active.job_title} · Pay date {fmtDate(active.pay_date)}</div>
                <div className="text-xs text-muted-foreground">Period {fmtDate(active.period_start)} → {fmtDate(active.period_end)}</div>
              </div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Earnings</div>
              <Row label="Regular pay" value={usd(active.regular_pay)} />
              <Row label="Overtime pay" value={usd(active.overtime_pay)} />
              <Row label="Gross pay" value={usd(active.gross_pay)} strong />
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1 mt-4">Withholdings</div>
              <Row label="Federal tax" value={`- ${usd(active.federal_tax)}`} />
              <Row label="State tax" value={`- ${usd(active.state_tax)}`} />
              <Row label="Social Security" value={`- ${usd(active.social_security)}`} />
              <Row label="Medicare" value={`- ${usd(active.medicare)}`} />
              <Row label="Health insurance" value={`- ${usd(active.health_deduction)}`} />
              <Row label="401(k)" value={`- ${usd(active.retirement)}`} />
              <div className="border-t border-border mt-3 pt-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg px-3">
                <Row label="Net Pay" value={usd(active.net_pay)} strong accent="text-emerald-600" />
              </div>
              <Button data-testid="payslip-download-pdf-btn" className="w-full mt-4" onClick={() => download(active)}>
                <Download className="w-4 h-4 mr-1.5" /> Download Paystub (PDF)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
